/**
 * The operator's own identity in a room — nothing to do with cameras/producers/slots
 * (see `consumer-slots.ts` for that). Two identity strategies: a fresh in-memory id
 * every call (the default — safe when more than one independent session in the same
 * tab targets the same room, since there's nothing to collide on), or a
 * `sessionStorage`-cached id per room so a page reload reconnects as the SAME peer
 * (opt-in via `startOperatorSession`'s `persistPeerId`).
 */

import { v4 } from "uuid";

const PEER_ID_STORAGE_PREFIX = "teleop-peerId:";

export interface GetOrCreatePeerIdOptions {
  sessionStorage?: Storage;
}

function defaultSessionStorage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** `operator-` prefix lets `consumer-slots.ts`'s `mountFromPeerId` tell the operator's
 * own peer id apart from a camera/mount peer id at a glance, without a lookup. */
export function makePeerId(): string {
  return `operator-${v4()}`;
}

export function getOrCreatePeerId(sessionId: string, opts: GetOrCreatePeerIdOptions = {}): string {
  const key = `${PEER_ID_STORAGE_PREFIX}${sessionId}`;
  const storage = opts.sessionStorage ?? defaultSessionStorage();
  try {
    const existing = storage?.getItem?.(key);
    if (existing) return existing;
    const id = makePeerId();
    storage?.setItem?.(key, id);
    return id;
  } catch {
    return makePeerId();
  }
}
